import { ContextBand } from '@today/ui-domain'
import { createContext, type ReactElement, type ReactNode, useContext, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useViewerPermissions } from '@/auth/useViewerPermissions'
import { useViewerRole } from '@/auth/useViewerRole'
import { planeBasePath } from '../usePlaneTargets'
import { deriveStepBand, homePlaneForRole, planeForPath, type StepBand } from './deriveStepBand'

/**
 * The shell session (frontend-architecture.md §2). In Wave 1 it is only ever
 * `direct` (the viewer is on their natural plane) or `step` (the viewer is viewing a
 * narrower plane they already own). The `enter`/`actas` modes are server-authoritative
 * and blocked on AD-5 (ADR-0064) — this provider never produces them.
 */
export type ShellSession = { mode: 'direct' } | StepBand

/** The step band's leading copy + Back action label (mockup `BANDS.step`). */
const STEP_BAND_COPY = { title: 'Viewing a narrower plane', action: 'Back to admin' } as const

interface ShellSessionValue {
	session: ShellSession
	/** The context-band node to pass into `PlaneShellFrame`'s band slot, or `null`. */
	band: ReactNode
}

const ShellSessionContext = createContext<ShellSessionValue | null>(null)

/**
 * Provides the shell session derived purely from the current route and the viewer's
 * role (frontend-architecture.md §2). It reads `useLocation` + `useViewerRole`, so it
 * MUST be mounted inside the router, around a plane shell.
 *
 * @remarks
 * - **`step` only.** The session is `step` when the current route's plane is strictly
 *   narrower than the viewer's home plane, else `direct`. It never yields
 *   `enter`/`actas` (blocked on AD-5) — the impersonation gap fails closed.
 * - **Exposes a ready band node.** For a `step` session it builds a
 *   `<ContextBand mode="step" …/>` whose Back action navigates to the viewer's home
 *   plane; for `direct` the band is `null`. A shell reads {@link useShellSession} and
 *   passes `band` straight into `PlaneShellFrame`'s band slot.
 */
export function ShellSessionProvider({ children }: { children: ReactNode }): ReactElement {
	const { pathname } = useLocation()
	const navigate = useNavigate()
	const { role } = useViewerRole()
	const { tenantSlug } = useViewerPermissions()

	const value = useMemo<ShellSessionValue>(() => {
		const home = homePlaneForRole(role)
		const step = deriveStepBand(home, planeForPath(pathname))
		if (!step) return { session: { mode: 'direct' }, band: null }

		// `home` (not `step.home`) keeps TS's non-null narrowing across the closure.
		const backToHome = (): void => {
			void navigate(planeBasePath(home, tenantSlug))
		}
		const band = (
			<ContextBand mode="step" title={STEP_BAND_COPY.title} action={STEP_BAND_COPY.action} onAction={backToHome} />
		)
		return { session: step, band }
	}, [role, pathname, tenantSlug, navigate])

	return <ShellSessionContext.Provider value={value}>{children}</ShellSessionContext.Provider>
}

/**
 * Reads the current {@link ShellSession} and the band node to render. Must be called
 * under a {@link ShellSessionProvider}; throws otherwise so a missing provider is a
 * loud wiring error, not a silently-absent band.
 */
export function useShellSession(): ShellSessionValue {
	const value = useContext(ShellSessionContext)
	if (!value) throw new Error('useShellSession must be used within a ShellSessionProvider')
	return value
}
